import * as yup from 'yup';
import { getZipCodeValidationSchema } from '../../utils/locationsHelper';
import { ServiceFocus } from '../../slices/service-focuses/models/service-focus';
import { getOtherFocus } from '../../containers/UserPages/Login/Modals/PracticeRegistrationForm/utils';

export const getPracticeLocationSchema = (
    axios: any,
    zipCodeCache: Record<string, boolean>,
    serviceFocuses: ServiceFocus[]
) =>
    yup.object().shape({
        locationName: yup.string().trim().required('Location Name is required'),
        addressInfo: yup.object().shape({
            streetAddress: yup
                .string()
                .trim()
                .required('Street Address is required'),
            city: yup.string().trim().required('City is required'),
            state: yup.string().trim().required('State is required'),
            zipCode: getZipCodeValidationSchema(axios, zipCodeCache),
        }),
        locationType: yup.mixed().required('Location Type is required'),
        dbaName: yup.string().trim().required(),
        fteDvms: yup
            .number()
            .required('Location FTE DVMs is required')
            .min(0.5, 'Location FTE DVMs cannot be negative'),
        practiceFocusType: yup
            .number()
            .required('Service Focus Type is required'),
        otherPracticeType: yup
            .string()
            .trim()
            .when('practiceFocusType', {
                is: getOtherFocus(serviceFocuses)?.id,
                then: (schema) =>
                    schema.required('Please specify the service focus type'),
                otherwise: (schema) => schema.notRequired(),
            }),
        ratings: yup.object().shape({
            google: ratingSchema,
            googleReviewCount: reviewCountSchema,
            facebook: ratingSchema,
            facebookReviewCount: reviewCountSchema,
            yelp: ratingSchema,
            yelpReviewCount: reviewCountSchema,
        }),
    });

export const getPracticeProfileSchema = (
    axios: any,
    zipCodeCache: Record<string, boolean>,
    serviceFocuses: ServiceFocus[]
) =>
    yup.object().shape({
        practiceName: yup.string().trim().required('Practice Name is required'),
        ownerFullName: yup
            .string()
            .trim()
            .required('Owner Full Name is required'),
        practiceOwnershipType: yup
            .mixed()
            .required('Practice Ownership Type is required'),
        revenueGrowthRate: yup
            .number()
            .required('Revenue Growth Rate is required'),
        fteDvms: yup
            .number()
            .required('FTE DVMs is required')
            .min(0.5, 'FTE DVMs cannot be negative'),
        practiceLocations: yup
            .array()
            .of(getPracticeLocationSchema(axios, zipCodeCache, serviceFocuses))
            .default([]),
        practiceFocusType: yup.number().required('Service focus is required'),
        otherPracticeType: yup
            .string()
            .trim()
            .when('practiceFocusType', {
                is: getOtherFocus(serviceFocuses)?.id,
                then: (schema) =>
                    schema.required('Please specify the service focus type'),
                otherwise: (schema) => schema.notRequired(),
            }),
        averageRating: ratingSchema,
    });

const ratingSchema = yup
    .number()
    .nullable()
    .transform((v) => (v === '' || v == null ? null : v))
    .min(0, 'Rating must be from 0 to 5')
    .max(5, 'Rating must be from 0 to 5');

const reviewCountSchema = yup
    .number()
    .nullable()
    .transform((v) => (v === '' || v == null ? null : v))
    .integer('Review count must be a whole number')
    .min(0, 'Review count must be non-negative');

export const getSellerProfileValidationSchema = (
    axios: any,
    zipCodeCache: Record<string, boolean>,
    serviceFocuses: ServiceFocus[]
) =>
    yup.object().shape({
        practiceProfiles: yup
            .array()
            .of(getPracticeProfileSchema(axios, zipCodeCache, serviceFocuses))
            .default([]),
    });

export const getSellerDocsInfoValidationSchema = (
    axios: any,
    zipCodeCache: Record<string, boolean>
) =>
    yup.object().shape({
        fullLegalEntityName: yup
            .string()
            .trim()
            .required('Full Legal Entity Name is required'),
        legalEntityType: yup
            .number()
            .required('Type of Legal Entity is required'),
        legalEntityRegistrationState: yup
            .string()
            .trim()
            .required('State of Registration is required'),
        legalEntityAddress: yup.object().shape({
            streetAddress: yup
                .string()
                .trim()
                .required('Street Address is required'),
            city: yup.string().trim().required('City is required'),
            state: yup.string().trim().required('State is required'),
            zipCode: getZipCodeValidationSchema(axios, zipCodeCache),
        }),
        recipientFullName: yup
            .string()
            .trim()
            .required('Recipient Full Name is required'),
        recipientTitle: yup
            .string()
            .trim()
            .required('Recipient Title is required'),
        recipientAddressInfo: yup.object().shape({
            streetAddress: yup
                .string()
                .trim()
                .required('Street Address is required'),
            city: yup.string().trim().required('City is required'),
            state: yup.string().trim().required('State is required'),
            zipCode: getZipCodeValidationSchema(axios, zipCodeCache),
        }),
    });
